import { FC, useEffect, useMemo, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { Alert, Badge, Button, ButtonGroup, Card, Col, Dropdown, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getCaresShellBootstrap } from '../shell/bootstrap';
import CaresPageHeader from '../shell/cares-page-header';
import {
  fetchGroupScheduleVisibilityContext,
  fetchPublishedGroupScheduleDetail,
  fetchPublishedGroupSchedules,
  type GroupScheduleGridRow,
  type GroupScheduleVisibilityBucket,
  type GroupScheduleVisibilityContext,
  type PublishedGroupScheduleCatalogResponse,
  type PublishedGroupScheduleDetailResponse,
} from './api';
import {
  defaultScheduleViewPreferences,
  fetchScheduleViewPreferences,
  updateLastSelectedSchedule,
  updateScheduleViewPreferences,
  type ScheduleViewDensity,
  type ScheduleViewPreferences,
} from './view-preferences-api';
import './group-schedule-shell.scss';

const bucketToneClass: Record<GroupScheduleVisibilityBucket, string> = {
  assigned: 'bg-primary-transparent text-primary',
  adjacent: 'bg-info-transparent text-info',
  browse_only: 'bg-warning-transparent text-warning',
};

const emptyCatalog: PublishedGroupScheduleCatalogResponse = {
  selectedCompany: { id: 0, name: '', slug: '' },
  resolvedScope: { key: 'assigned_only', source: 'membership_scope_fallback' },
  filters: {
    bucket: null,
    search: null,
    scheduleType: null,
    serviceLine: null,
    location: null,
  },
  data: [],
  meta: {
    count: 0,
    canExport: false,
    canEdit: false,
    source: 'group_visibility_scope',
  },
};

const currentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const shiftMonthKey = (monthKey: string, offset: number) => {
  const [year, month] = monthKey.split('-').map(Number);
  const next = new Date(year, month - 1 + offset, 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
};

const gridCellClassName = (status: 'assigned' | 'open' | 'not_scheduled' | 'private') => {
  if (status === 'assigned') return 'bg-primary-transparent';
  if (status === 'open') return 'bg-warning-transparent';
  if (status === 'private') return 'bg-danger-transparent';
  return 'bg-light';
};

const privacyBadgeClass = (mode: 'full' | 'masked' | 'hidden') => {
  if (mode === 'full') return 'bg-success-transparent text-success';
  if (mode === 'masked') return 'bg-warning-transparent text-warning';
  return 'bg-danger-transparent text-danger';
};

const findCell = (row: GroupScheduleGridRow, columnKey: string) =>
  row.cells.find((cell) => cell.columnKey === columnKey) ?? null;

const compactCellLabel = (displayName: string) => {
  if (displayName === 'Open coverage') return 'Open';
  if (nonPhysicianLabels.has(displayName)) return '—';

  const parts = displayName
    .replace(/^(dr\.?|doctor)\s+/i, '')
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const uniqueOptions = (values: Array<string | null | undefined>) =>
  Array.from(new Set(values.filter((value): value is string => Boolean(value)))).sort((left, right) => left.localeCompare(right));

const displayScheduleName = (name: string) => name.replace(/\s+schedule$/i, '').trim();

const nonPhysicianLabels = new Set([
  'Identity hidden',
  'Name masked',
  'No shift scheduled',
  'Open coverage',
  'Outside month',
  'Private assignment',
  'Weekend only',
]);

const GroupScheduleShell: FC = () => {
  const bootstrap = getCaresShellBootstrap();
  const location = useLocation();
  const navigate = useNavigate();
  const [context, setContext] = useState<GroupScheduleVisibilityContext | null>(null);
  const [catalog, setCatalog] = useState<PublishedGroupScheduleCatalogResponse>(emptyCatalog);
  const [detailResponse, setDetailResponse] = useState<PublishedGroupScheduleDetailResponse | null>(null);
  const [viewPreferences, setViewPreferences] = useState<ScheduleViewPreferences>(defaultScheduleViewPreferences);
  const [viewPreferencesLoaded, setViewPreferencesLoaded] = useState(false);
  const [loadingContext, setLoadingContext] = useState(true);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [showShortcutCustomizer, setShowShortcutCustomizer] = useState(false);
  const [shortcutDraft, setShortcutDraft] = useState<string[]>([]);
  const [savingShortcuts, setSavingShortcuts] = useState(false);
  const [shortcutError, setShortcutError] = useState<string | null>(null);

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const selectedCompany = query.get('company') ?? undefined;
  const requestedBucket = query.get('bucket') as GroupScheduleVisibilityBucket | null;
  const selectedScheduleId = query.get('schedule') ?? '';
  const selectedMonth = query.get('month') ?? currentMonthKey();
  const requestedDensity = query.get('view');
  const calendarDensity: ScheduleViewDensity = requestedDensity === 'compact' || requestedDensity === 'standard'
    ? requestedDensity
    : viewPreferences.density;
  const providerView = requestedDensity === 'provider';
  const schedulerWorkspace = location.pathname.startsWith('/scheduling/');
  const selectedDate = schedulerWorkspace ? query.get('date') ?? '' : '';
  const selectedLocation = schedulerWorkspace ? query.get('location') ?? '' : '';
  const selectedPhysician = schedulerWorkspace ? query.get('physician') ?? '' : '';
  const selectedService = schedulerWorkspace ? query.get('service') ?? '' : '';
  const selectedRole = schedulerWorkspace ? query.get('role') ?? '' : '';
  const selectedShift = schedulerWorkspace ? query.get('shift') ?? '' : '';
  const groupScheduleHeader = <CaresPageHeader homepage={schedulerWorkspace ? 'Scheduling' : 'My Schedule'} activepage={schedulerWorkspace ? 'Scheduling' : 'My Schedule'} page="Department Schedule" />;

  const replaceSearch = (mutate: (params: URLSearchParams) => void) => {
    const params = new URLSearchParams(location.search);
    mutate(params);
    const nextSearch = params.toString();

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (!bootstrap.authenticated) return;
    const controller = new AbortController();

    fetchScheduleViewPreferences(controller.signal)
      .then(setViewPreferences)
      .catch(() => {
        if (!controller.signal.aborted) setViewPreferences(defaultScheduleViewPreferences);
      })
      .finally(() => {
        if (!controller.signal.aborted) setViewPreferencesLoaded(true);
      });

    return () => controller.abort();
  }, [bootstrap.authenticated]);

  useEffect(() => {
    let cancelled = false;

    const loadContext = async () => {
      setLoadingContext(true);
      setError(null);

      try {
        const payload = await fetchGroupScheduleVisibilityContext({ company: selectedCompany });

        if (cancelled) {
          return;
        }

        setContext(payload);

        const enabledBuckets = payload.availableFilters.visibilityBuckets.filter((bucket) => bucket.enabled);
        const nextBucket = requestedBucket && enabledBuckets.some((bucket) => bucket.key === requestedBucket)
          ? requestedBucket
          : enabledBuckets[0]?.key ?? payload.availableFilters.defaultBucket;

        if (nextBucket !== requestedBucket || payload.selectedCompany.slug !== selectedCompany) {
          replaceSearch((params) => {
            params.set('bucket', nextBucket);
            params.set('company', payload.selectedCompany.slug);
            if (!params.get('month')) {
              params.set('month', currentMonthKey());
            }
          });
        }
      } catch (requestError) {
        if (!cancelled) {
          setContext(null);
          setCatalog(emptyCatalog);
          setError(requestError instanceof Error ? requestError.message : 'Unable to load department schedule visibility.');
        }
      } finally {
        if (!cancelled) {
          setLoadingContext(false);
        }
      }
    };

    void loadContext();

    return () => {
      cancelled = true;
    };
  }, [requestedBucket, selectedCompany]);

  useEffect(() => {
    if (!context) {
      setLoadingCatalog(false);
      return;
    }

    let cancelled = false;

    const loadCatalog = async () => {
      setLoadingCatalog(true);
      setError(null);

      try {
        const payload = await fetchPublishedGroupSchedules({
          company: context.selectedCompany.slug,
          bucket: requestedBucket ?? undefined,
        });

        if (!cancelled) {
          setCatalog(payload);
        }
      } catch (requestError) {
        if (!cancelled) {
          setCatalog(emptyCatalog);
          setError(requestError instanceof Error ? requestError.message : 'Unable to load published department schedules.');
        }
      } finally {
        if (!cancelled) {
          setLoadingCatalog(false);
        }
      }
    };

    void loadCatalog();

    return () => {
      cancelled = true;
    };
  }, [context, requestedBucket]);

  const enabledBuckets = context?.availableFilters.visibilityBuckets.filter((bucket) => bucket.enabled) ?? [];
  const locationOptions = Array.from(
    new Map(
      catalog.data
        .filter((record) => record.publishedSchedule.location)
        .map((record) => [String(record.publishedSchedule.location!.id), record.publishedSchedule.location!.name])
    )
  );
  const serviceOptions = Array.from(
    new Map(
      catalog.data
        .filter((record) => record.publishedSchedule.serviceLine)
        .map((record) => [String(record.publishedSchedule.serviceLine!.id), record.publishedSchedule.serviceLine!.name])
    )
  );
  const filteredCatalogRecords = catalog.data.filter((record) =>
    (!selectedLocation || String(record.publishedSchedule.location?.id ?? '') === selectedLocation)
    && (!selectedService || String(record.publishedSchedule.serviceLine?.id ?? '') === selectedService)
  );
  const groupedCatalog = enabledBuckets.map((bucket) => ({
    ...bucket,
    records: filteredCatalogRecords.filter((record) => record.visibilityBucket === bucket.key),
  }));
  const activeBucket = requestedBucket && enabledBuckets.some((bucket) => bucket.key === requestedBucket)
    ? requestedBucket
    : enabledBuckets[0]?.key ?? null;
  const activeBucketGroup = activeBucket
    ? groupedCatalog.find((bucket) => bucket.key === activeBucket) ?? null
    : null;
  const selectedRecord = activeBucketGroup?.records.find((record) => record.id === selectedScheduleId) ?? null;
  const shortcutRecords = activeBucketGroup?.records.filter((record) => (
    viewPreferences.pinnedScheduleIds === null || viewPreferences.pinnedScheduleIds.includes(record.id)
  )) ?? [];
  const bucketCount = (key: GroupScheduleVisibilityBucket) =>
    groupedCatalog.find((bucket) => bucket.key === key)?.records.length ?? 0;

  useEffect(() => {
    if (!viewPreferencesLoaded || !activeBucketGroup || activeBucketGroup.records.length === 0) {
      return;
    }

    if (!selectedRecord) {
      const rememberedRecord = selectedScheduleId
        ? null
        : activeBucketGroup.records.find(record => record.id === viewPreferences.lastSelectedScheduleId) ?? null;
      const nextRecord = rememberedRecord ?? activeBucketGroup.records[0];
      replaceSearch((params) => {
        params.set('schedule', nextRecord.id);
      });
      if (!rememberedRecord) {
        void updateLastSelectedSchedule(nextRecord.id).catch(() => undefined);
      }
    }
  }, [activeBucketGroup, selectedRecord, selectedScheduleId, viewPreferences.lastSelectedScheduleId, viewPreferencesLoaded]);

  const selectSchedule = (scheduleId: string) => {
    setViewPreferences(current => ({ ...current, lastSelectedScheduleId: scheduleId }));
    void updateLastSelectedSchedule(scheduleId).catch(() => undefined);
    replaceSearch((params) => params.set('schedule', scheduleId));
  };

  useEffect(() => {
    if (!selectedRecord) {
      setDetailResponse(null);
      setLoadingDetail(false);
      setDetailError(null);
      return;
    }

    let cancelled = false;

    const loadDetail = async () => {
      setLoadingDetail(true);
      setDetailError(null);

      try {
        const payload = await fetchPublishedGroupScheduleDetail(selectedRecord.links.detail, {
          company: context?.selectedCompany.slug,
          month: selectedMonth,
          weekStart: viewPreferences.weekStart,
        });

        if (!cancelled) {
          setDetailResponse(payload);
        }
      } catch (requestError) {
        if (!cancelled) {
          setDetailResponse(null);
          setDetailError(requestError instanceof Error ? requestError.message : 'Unable to load schedule grid.');
        }
      } finally {
        if (!cancelled) {
          setLoadingDetail(false);
        }
      }
    };

    void loadDetail();

    return () => {
      cancelled = true;
    };
  }, [context?.selectedCompany.slug, selectedMonth, selectedRecord?.id, viewPreferences.weekStart]);

  const updateMonth = (monthKey: string) => {
    replaceSearch((params) => {
      params.set('month', monthKey);
    });
  };

  const detailGrid = detailResponse?.data.scheduleGrid ?? null;
  const physicianOptions = detailGrid
    ? uniqueOptions(detailGrid.rows.flatMap((row) => row.cells.map((cell) => cell.displayName)).filter((label) => !nonPhysicianLabels.has(label)))
    : [];
  const roleOptions = detailGrid ? uniqueOptions(detailGrid.rows.map((row) => row.coverageLabel)) : [];
  const shiftOptions = detailGrid?.rows ?? [];
  const visibleColumns = detailGrid?.columns.filter((column) => selectedDate ? column.date === selectedDate : true) ?? [];
  const visibleRows = detailGrid?.rows.filter((row) =>
    (!selectedShift || row.id === selectedShift)
    && (!selectedRole || row.coverageLabel === selectedRole)
  ) ?? [];
  const calendarColumnGroups = calendarDensity === 'standard'
    ? Array.from({ length: Math.ceil(visibleColumns.length / 7) }, (_, index) => visibleColumns.slice(index * 7, (index + 1) * 7))
    : [visibleColumns];
  const providerSelection = selectedPhysician || physicianOptions[0] || '';
  const providerEvents = detailGrid && providerSelection
    ? detailGrid.rows.flatMap((row) => detailGrid.columns.flatMap((column) => {
        const cell = findCell(row, column.key);
        if (!cell || cell.assignmentStatus !== 'assigned' || cell.displayName !== providerSelection) return [];

        return [{
          id: `${row.id}-${column.key}`,
          title: `${row.label}${row.timeLabel ? ` · ${row.timeLabel}` : ''}`,
          start: column.date,
          allDay: true,
        }];
      }))
    : [];
  const updateSchedulerFilter = (key: 'date' | 'location' | 'physician' | 'service' | 'role' | 'shift', value: string) => {
    replaceSearch((params) => {
      if (value) params.set(key, value); else params.delete(key);
      if (key === 'location' || key === 'service') params.delete('schedule');
    });
  };

  const clearSchedulerFilters = () => {
    replaceSearch((params) => {
      ['date', 'location', 'physician', 'service', 'role', 'shift'].forEach((key) => params.delete(key));
      params.delete('schedule');
    });
  };

  const openShortcutCustomizer = () => {
    const availableIds = activeBucketGroup?.records.map((record) => record.id) ?? [];
    setShortcutDraft(viewPreferences.pinnedScheduleIds === null
      ? availableIds
      : viewPreferences.pinnedScheduleIds.filter((id) => availableIds.includes(id)));
    setShortcutError(null);
    setShowShortcutCustomizer(true);
  };

  const saveScheduleShortcuts = async () => {
    setSavingShortcuts(true);
    setShortcutError(null);

    try {
      const saved = await updateScheduleViewPreferences({
        ...viewPreferences,
        pinnedScheduleIds: shortcutDraft,
      });
      setViewPreferences(saved);
      setShowShortcutCustomizer(false);
    } catch (requestError) {
      setShortcutError(requestError instanceof Error ? requestError.message : 'Unable to save schedule shortcuts.');
    } finally {
      setSavingShortcuts(false);
    }
  };

  if (!bootstrap.authenticated) {
    return (
      <>
        {groupScheduleHeader}
        <Alert variant="danger" className="custom-card">
          This department schedule workspace requires an authenticated CARES session.
          <Link to="/login" className="alert-link ms-1">
            Sign in
          </Link>
          to continue.
        </Alert>
      </>
    );
  }

  if (schedulerWorkspace ? !bootstrap.navigation.schedulerReview : !bootstrap.navigation.mySchedule) {
    return (
      <>
        {groupScheduleHeader}
        <Alert variant="warning" className="custom-card">
          Department Schedule is only available inside the CARES scheduling workspace for active tenant members.
        </Alert>
      </>
    );
  }

  return (
    <>
      {groupScheduleHeader}

      <Card className="custom-card">
        <Card.Body className="d-flex flex-column gap-4">
          <div className="border rounded-3 p-3 p-xl-4 bg-light">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
              <div className="d-flex flex-wrap gap-2 align-items-center" role="group" aria-label="Department schedule selection">
                <Dropdown>
                  <Dropdown.Toggle variant="light" className="border px-3 py-2">
                    All schedules
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {activeBucketGroup?.records.map((record) => (
                      <Dropdown.Item
                        key={record.id}
                        active={selectedRecord?.id === record.id}
                        onClick={() => selectSchedule(record.id)}
                      >
                        {displayScheduleName(record.group.name)}
                      </Dropdown.Item>
                    ))}
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={openShortcutCustomizer}>Customize shortcuts…</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                {shortcutRecords.map((record) => (
                  <Button
                    key={record.id}
                    type="button"
                    variant={selectedRecord?.id === record.id ? 'primary' : 'light'}
                    className="text-start border px-3 py-2"
                    onClick={() => selectSchedule(record.id)}
                  >
                    <span className="fw-semibold">{displayScheduleName(record.group.name)}</span>
                  </Button>
                ))}
              </div>
              <div className="d-flex flex-wrap gap-2 justify-content-lg-end" role="group" aria-label="Department schedule visibility buckets">
                {enabledBuckets.map((bucket) => (
                  <Button
                    key={bucket.key}
                    type="button"
                    variant=""
                    className={`text-nowrap ${bucketToneClass[bucket.key]} ${activeBucket === bucket.key ? 'fw-semibold' : ''}`}
                    aria-pressed={activeBucket === bucket.key}
                    onClick={() =>
                      replaceSearch((params) => {
                        params.set('bucket', bucket.key);
                        params.delete('schedule');
                      })
                    }
                  >
                    {bucket.label} ({bucketCount(bucket.key)})
                  </Button>
                ))}
              </div>
            </div>

            {schedulerWorkspace ? (
              <div className="border-top mt-3 pt-3" aria-labelledby="scheduler-group-schedule-filters">
                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2 mb-3">
                  <h6 id="scheduler-group-schedule-filters" className="mb-0">Schedule filters</h6>
                  <Button type="button" variant="light" size="sm" onClick={clearSchedulerFilters}>
                    Reset scheduler filters
                  </Button>
                </div>
                <Row className="g-3">
                  <Col xl={2} md={4} sm={6}>
                    <Form.Label htmlFor="group-schedule-date" className="text-uppercase text-muted fs-12 fw-semibold mb-2">Date</Form.Label>
                    <Form.Control id="group-schedule-date" type="date" value={selectedDate} onChange={(event) => updateSchedulerFilter('date', event.target.value)} />
                  </Col>
                  <Col xl={2} md={4} sm={6}>
                    <Form.Label htmlFor="group-schedule-location" className="text-uppercase text-muted fs-12 fw-semibold mb-2">Location</Form.Label>
                    <Form.Select id="group-schedule-location" value={selectedLocation} onChange={(event) => updateSchedulerFilter('location', event.target.value)}>
                      <option value="">All locations</option>
                      {locationOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </Form.Select>
                  </Col>
                  <Col xl={2} md={4} sm={6}>
                    <Form.Label htmlFor="group-schedule-physician" className="text-uppercase text-muted fs-12 fw-semibold mb-2">Physician</Form.Label>
                    <Form.Select id="group-schedule-physician" value={selectedPhysician} onChange={(event) => updateSchedulerFilter('physician', event.target.value)} disabled={!detailGrid}>
                      <option value="">All physicians</option>
                      {physicianOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                    </Form.Select>
                  </Col>
                  <Col xl={2} md={4} sm={6}>
                    <Form.Label htmlFor="group-schedule-service" className="text-uppercase text-muted fs-12 fw-semibold mb-2">Service</Form.Label>
                    <Form.Select id="group-schedule-service" value={selectedService} onChange={(event) => updateSchedulerFilter('service', event.target.value)}>
                      <option value="">All services</option>
                      {serviceOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </Form.Select>
                  </Col>
                  <Col xl={2} md={4} sm={6}>
                    <Form.Label htmlFor="group-schedule-role" className="text-uppercase text-muted fs-12 fw-semibold mb-2">Role</Form.Label>
                    <Form.Select id="group-schedule-role" value={selectedRole} onChange={(event) => updateSchedulerFilter('role', event.target.value)} disabled={!detailGrid}>
                      <option value="">All roles</option>
                      {roleOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                    </Form.Select>
                  </Col>
                  <Col xl={2} md={4} sm={6}>
                    <Form.Label htmlFor="group-schedule-shift" className="text-uppercase text-muted fs-12 fw-semibold mb-2">Shift</Form.Label>
                    <Form.Select id="group-schedule-shift" value={selectedShift} onChange={(event) => updateSchedulerFilter('shift', event.target.value)} disabled={!detailGrid}>
                      <option value="">All shifts</option>
                      {shiftOptions.map((row) => <option key={row.id} value={row.id}>{row.label}</option>)}
                    </Form.Select>
                  </Col>
                </Row>
              </div>
            ) : null}
          </div>

          {error ? <Alert variant="warning">{error}</Alert> : null}

          {loadingContext || loadingCatalog ? (
            <div className="text-center p-5">
              <Spinner animation="border" />
            </div>
          ) : activeBucketGroup?.records.length ? (
            selectedRecord ? (
                <Card className="border shadow-none mb-0">
                  <Card.Header className="justify-content-between align-items-center gap-3 flex-wrap">
                    <Card.Title className="mb-0">{displayScheduleName(selectedRecord.group.name)}</Card.Title>
                    <div className="d-flex flex-wrap gap-2 justify-content-end">
                      <Button type="button" size="sm" variant="light" onClick={() => updateMonth(shiftMonthKey(selectedMonth, -1))}>
                        Previous
                      </Button>
                      <Button type="button" size="sm" variant="light" onClick={() => updateMonth(currentMonthKey())}>
                        Current
                      </Button>
                      <Button type="button" size="sm" variant="light" onClick={() => updateMonth(shiftMonthKey(selectedMonth, 1))}>
                        Next
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {loadingDetail ? (
                      <div className="text-center p-5">
                        <Spinner animation="border" />
                      </div>
                    ) : null}

                    {!loadingDetail && detailError ? <Alert variant="warning">{detailError}</Alert> : null}

                    {!loadingDetail && detailResponse ? (
                      visibleColumns.length === 0 || visibleRows.length === 0 ? (
                        <Alert variant="light" className="mb-0">No schedule cells match the active scheduler filters.</Alert>
                      ) : (
                        <div className="d-flex flex-column gap-3">
                          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2">
                            {providerView ? (
                              <Form.Select
                                aria-label="Provider calendar"
                                className="w-auto"
                                value={providerSelection}
                                onChange={(event) => updateSchedulerFilter('physician', event.target.value)}
                              >
                                {physicianOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                              </Form.Select>
                            ) : <span />}
                            <ButtonGroup size="sm" aria-label="Calendar display view">
                              <Button
                                type="button"
                                variant={!providerView && calendarDensity === 'standard' ? 'primary' : 'outline-primary'}
                                onClick={() => replaceSearch((params) => params.set('view', 'standard'))}
                              >
                                Standard
                              </Button>
                              <Button
                                type="button"
                                variant={!providerView && calendarDensity === 'compact' ? 'primary' : 'outline-primary'}
                                onClick={() => replaceSearch((params) => params.set('view', 'compact'))}
                              >
                                Compact
                              </Button>
                              <Button
                                type="button"
                                variant={providerView ? 'primary' : 'outline-primary'}
                                onClick={() => replaceSearch((params) => params.set('view', 'provider'))}
                              >
                                Provider
                              </Button>
                            </ButtonGroup>
                          </div>
                          {providerView ? (
                            providerSelection ? (
                              <div className="cares-calendar">
                                <FullCalendar
                                  key={`${selectedRecord.id}-${selectedMonth}-${providerSelection}`}
                                  plugins={[dayGridPlugin]}
                                  initialView="dayGridMonth"
                                  initialDate={`${selectedMonth}-01`}
                                  headerToolbar={false}
                                  events={providerEvents}
                                  dayMaxEvents
                                  fixedWeekCount
                                  height="auto"
                                />
                              </div>
                            ) : <Alert variant="light" className="mb-0">No named providers are available in this schedule.</Alert>
                          ) : (
                            <div className="d-flex flex-column gap-3">
                              {calendarColumnGroups.map((columns, groupIndex) => (
                                <div key={columns[0]?.key ?? groupIndex} className="table-responsive border rounded-3 department-schedule-grid-wrap">
                                  <Table
                                    hover
                                    className={`align-middle mb-0 schedule-grid-table department-schedule-grid department-schedule-grid--${calendarDensity}`}
                                    aria-label={`${detailResponse.data.scheduleGrid.monthLabel} department schedule${calendarDensity === 'standard' ? ` week ${groupIndex + 1}` : ''}`}
                                  >
                                    <thead className="table-light">
                                      <tr>
                                        <th scope="col" className="department-schedule-grid__shift text-nowrap px-2 py-2">Shift</th>
                                        {columns.map((column) => (
                                          <th key={column.key} scope="col" className={`department-schedule-grid__date px-2 py-2 text-center ${column.isWeekend ? 'bg-warning-transparent' : ''} ${column.isCurrentMonth ? '' : 'text-muted opacity-50'}`}>
                                            <span className="text-nowrap">{column.shortLabel}</span>
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {visibleRows.map((row) => (
                                        <tr key={row.id}>
                                          <th scope="row" className="department-schedule-grid__shift bg-light px-2 py-2" title={row.coverageLabel}>
                                            <div className="fw-semibold text-nowrap">{row.label}</div>
                                            {calendarDensity === 'standard' ? <div className="text-muted fs-11 fw-normal text-nowrap">{row.timeLabel}</div> : null}
                                          </th>
                                          {columns.map((column) => {
                                            const candidateCell = findCell(row, column.key);
                                            const cell = selectedPhysician && candidateCell?.displayName !== selectedPhysician ? null : candidateCell;
                                            return (
                                              <td key={`${row.id}-${column.key}`} className={`p-1 ${column.isCurrentMonth ? '' : 'opacity-50'}`}>
                                                <div className={`department-schedule-grid__cell rounded-2 px-2 py-1 text-center ${gridCellClassName(cell?.assignmentStatus ?? 'not_scheduled')}`} title={cell?.displayName ?? 'Not scheduled'}>
                                                  <div className="fw-semibold fs-12 text-nowrap">{cell ? (calendarDensity === 'compact' ? compactCellLabel(cell.displayName) : cell.displayName) : '—'}</div>
                                                  {calendarDensity === 'standard' && cell?.secondaryLabel ? <div className="text-muted fs-11 text-nowrap">{cell.secondaryLabel}</div> : null}
                                                  {calendarDensity === 'standard' && cell && cell.privacyMode !== 'full' ? (
                                                    <Badge bg="" className={`mt-1 ${privacyBadgeClass(cell.privacyMode)}`}>{cell.privacyMode === 'masked' ? 'Masked' : 'Hidden'}</Badge>
                                                  ) : null}
                                                </div>
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    ) : null}
                  </Card.Body>
                </Card>
            ) : null
          ) : (
            <Alert variant="light" className="mb-0">
              No visible schedules matched this company and search scope in the active bucket.
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Modal show={showShortcutCustomizer} onHide={() => setShowShortcutCustomizer(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Customize schedule shortcuts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">Choose the schedules that appear as one-click buttons. Every authorized schedule remains in All schedules.</p>
          {shortcutError ? <Alert variant="warning">{shortcutError}</Alert> : null}
          <div className="d-flex justify-content-end gap-2 mb-3">
            <Button type="button" variant="light" size="sm" onClick={() => setShortcutDraft(activeBucketGroup?.records.map((record) => record.id) ?? [])}>Select all</Button>
            <Button type="button" variant="light" size="sm" onClick={() => setShortcutDraft([])}>Clear</Button>
          </div>
          <div className="d-flex flex-column gap-2">
            {activeBucketGroup?.records.map((record) => (
              <Form.Check
                key={record.id}
                id={`schedule-shortcut-${record.id}`}
                type="checkbox"
                label={displayScheduleName(record.group.name)}
                checked={shortcutDraft.includes(record.id)}
                onChange={(event) => setShortcutDraft((current) => event.target.checked
                  ? [...current, record.id]
                  : current.filter((id) => id !== record.id))}
              />
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="light" onClick={() => setShowShortcutCustomizer(false)} disabled={savingShortcuts}>Cancel</Button>
          <Button type="button" variant="primary" onClick={() => void saveScheduleShortcuts()} disabled={savingShortcuts}>
            {savingShortcuts ? 'Saving…' : 'Save shortcuts'}
          </Button>
        </Modal.Footer>
      </Modal>

    </>
  );
};

export default GroupScheduleShell;
